/**
 * ============================================================================
 * TWO FACTOR SETUP COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Component for setting up two-factor authentication
 */

import React, { useState, useEffect } from 'react';
import { useTwoFactor } from '../../hooks/useTwoFactor';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import Button from '../common/Button';
import Input from '../common/Input';
import Spinner from '../common/Spinner';
import Modal from '../common/Modal';
import QRCode from '../links/QRCode';

/**
 * Two Factor Setup Component
 * @param {Object} props
 * @param {Function} props.onSuccess - Success callback
 * @returns {JSX.Element}
 */
const TwoFactorSetup = ({ onSuccess }) => {
  const { generateSecret, verifySetup, enable2FA, loading, secret, backupCodes } = useTwoFactor();
  const { showSuccess, showError } = useToast();
  const { language } = useI18n();

  const [step, setStep] = useState(1); // 1: Generate, 2: Verify, 3: Complete
  const [verificationCode, setVerificationCode] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (step === 1) {
      handleGenerateSecret();
    }
  }, []);

  const handleGenerateSecret = async () => {
    try {
      const data = await generateSecret();
      setStep(2);
    } catch (error) {
      showError(
        error.message || (language === 'fr' ? 'Échec de génération du secret' : 'Failed to generate secret')
      );
    }
  };

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setErrors({ code: language === 'fr' ? 'Code à 6 chiffres requis' : '6-digit code required' });
      return;
    }

    try {
      await verifySetup(verificationCode);
      await enable2FA(verificationCode);
      setStep(3);
      setShowBackupCodes(true);
      showSuccess(
        language === 'fr' ? '2FA activé avec succès!' : '2FA enabled successfully!'
      );
      if (onSuccess) onSuccess();
    } catch (error) {
      setErrors({ code: error.message || (language === 'fr' ? 'Code invalide' : 'Invalid code') });
    }
  };

  const handleCopyBackupCodes = () => {
    if (backupCodes && backupCodes.length > 0) {
      const codesText = backupCodes.join('\n');
      navigator.clipboard.writeText(codesText);
      showSuccess(language === 'fr' ? 'Codes copiés!' : 'Codes copied!');
    }
  };

  if (step === 1 || loading) {
    return (
      <Card variant="glass" className="p-12 text-center">
        <Spinner size="lg" />
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
          {language === 'fr' ? 'Génération du secret 2FA...' : 'Generating 2FA secret...'}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Step 2: QR Code and Secret */}
      {step === 2 && secret && (
        <>
          <Card variant="glass" className="animate-fadeInUp">
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-xl font-black text-gray-900 dark:text-white mb-2 flex items-center justify-center gap-2">
                  <span className="text-2xl">🔐</span>
                  {language === 'fr' ? 'Configurer l\'authentification à deux facteurs' : 'Setup Two-Factor Authentication'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {language === 'fr'
                    ? 'Scannez le QR code avec votre application d\'authentification'
                    : 'Scan the QR code with your authenticator app'}
                </p>
              </div>

              {/* QR Code */}
              {secret.qrCodeUrl && (
                <div className="flex justify-center">
                  <QRCode url={secret.qrCodeUrl} size={200} />
                </div>
              )}

              {/* Manual Entry */}
              <Card variant="outlined" className="bg-gray-50 dark:bg-gray-800/50">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                  {language === 'fr' ? 'Ou entrez manuellement:' : 'Or enter manually:'}
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm font-mono text-gray-900 dark:text-white bg-white dark:bg-gray-700 p-2 rounded-lg">
                    {secret.secret}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(secret.secret);
                      showSuccess(language === 'fr' ? 'Secret copié!' : 'Secret copied!');
                    }}
                  >
                    📋
                  </Button>
                </div>
              </Card>

              {/* Verification Code Input */}
              <div>
                <Input
                  type="text"
                  label={language === 'fr' ? 'Code de vérification' : 'Verification Code'}
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setVerificationCode(value);
                    setErrors({});
                  }}
                  placeholder="000000"
                  error={errors.code}
                  maxLength={6}
                  className="text-center text-2xl font-mono tracking-widest"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {language === 'fr'
                    ? 'Entrez le code à 6 chiffres de votre application d\'authentification'
                    : 'Enter the 6-digit code from your authenticator app'}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => setStep(1)}
                >
                  {language === 'fr' ? 'Retour' : 'Back'}
                </Button>
                <Button
                  variant="gradient"
                  fullWidth
                  onClick={handleVerify}
                  loading={loading}
                  disabled={verificationCode.length !== 6}
                  className="bg-gradient-to-r from-blue-600 to-green-600 shadow-xl hover:shadow-2xl transition-all"
                >
                  {language === 'fr' ? 'Vérifier et activer' : 'Verify & Enable'} →
                </Button>
              </div>
            </div>
          </Card>
        </>
      )}

      {/* Step 3: Success with Backup Codes */}
      {step === 3 && (
        <Card variant="glass" className="animate-fadeInUp">
          <div className="space-y-6">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full mb-4 shadow-lg">
                <span className="text-3xl">✓</span>
              </div>
              <h3 className="text-xl font-black text-gray-900 dark:text-white mb-2">
                {language === 'fr' ? '2FA activé!' : '2FA Enabled!'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {language === 'fr'
                  ? 'Votre authentification à deux facteurs est maintenant active'
                  : 'Your two-factor authentication is now active'}
              </p>
            </div>

            {/* Backup Codes */}
            {backupCodes && backupCodes.length > 0 && (
              <Card variant="outlined" className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-black text-yellow-800 dark:text-yellow-200 mb-1 flex items-center gap-2">
                        <span className="text-xl">🔑</span>
                        {language === 'fr' ? 'Codes de sauvegarde' : 'Backup Codes'}
                      </h4>
                      <p className="text-xs text-yellow-700 dark:text-yellow-300">
                        {language === 'fr'
                          ? 'Sauvegardez ces codes dans un endroit sûr. Vous pouvez les utiliser si vous perdez l\'accès à votre application d\'authentification.'
                          : 'Save these codes in a safe place. You can use them if you lose access to your authenticator app.'}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {backupCodes.map((code, index) => (
                      <code
                        key={index}
                        className="text-sm font-mono text-gray-900 dark:text-white bg-white dark:bg-gray-700 p-2 rounded-lg text-center"
                      >
                        {code}
                      </code>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={handleCopyBackupCodes}
                    className="border-yellow-500 text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20"
                  >
                    📋 {language === 'fr' ? 'Copier tous les codes' : 'Copy All Codes'}
                  </Button>
                </div>
              </Card>
            )}
          </div>
        </Card>
      )}

      {/* Info Card */}
      <Card variant="outlined" className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <span className="text-2xl">💡</span>
          <div>
            <p className="font-bold text-blue-800 dark:text-blue-200 mb-1">
              {language === 'fr' ? 'Applications recommandées' : 'Recommended Apps'}
            </p>
            <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
              <li>• Google Authenticator</li>
              <li>• Microsoft Authenticator</li>
              <li>• Authy</li>
              <li>• 1Password</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default TwoFactorSetup;
